// conventions-allow: file-size — which cells an edit writes is answered beside the gestures that arm replace-on-type; its listeners are a separate extraction.
import type { RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CellCoordinate, MouseEventLike, TableCellAdapter } from '../HourlyDataTable.types'

type UseTableCellSelectionOptions = {
  /** Strategy adapter — maps cell keys <-> grid coordinates */
  adapter: TableCellAdapter
  tableContainerRef: RefObject<HTMLDivElement | null>
}

export type UseTableCellSelectionResult = {
  selectedCellKeys: Set<string>
  anchorCellKey: string | null
  selectedCellKeysRef: React.MutableRefObject<Set<string>>
  anchorCellKeyRef: React.MutableRefObject<string | null>
  /** Snapshot ref safe to read from async clipboard handlers */
  selectedCellKeysForClipboardRef: React.MutableRefObject<Set<string>>
  /** Snapshot ref safe to read from async clipboard handlers */
  anchorCellKeyForClipboardRef: React.MutableRefObject<string | null>
  bulkReplaceOnNextInputRef: React.MutableRefObject<boolean>
  setAnchorCellKey: (key: string | null) => void
  replaceSelection: (nextSelection: Set<string>) => void
  updateSelection: (updater: (previous: Set<string>) => Set<string>) => void
  clearSelection: () => void
  handleCellMouseDown: (cellKey: string, ev: MouseEventLike) => void
  handleCellMouseOver: (cellKey: string) => void
  getEditTargets: (cellKey: string) => ReadonlySet<string>
  takeReplaceOnType: (cellKey: string) => boolean
}

export function useTableCellSelection({
  adapter,
  tableContainerRef,
}: UseTableCellSelectionOptions): UseTableCellSelectionResult {
  const [selectedCellKeys, setSelectedCellKeys] = useState<Set<string>>(new Set())
  const [anchorCellKey, setAnchorCellKeyState] = useState<string | null>(null)

  const selectedCellKeysRef = useRef<Set<string>>(new Set())
  const anchorCellKeyRef = useRef<string | null>(null)
  const selectedCellKeysForClipboardRef = useRef<Set<string>>(new Set())
  const anchorCellKeyForClipboardRef = useRef<string | null>(null)
  const dragAnchorRef = useRef<CellCoordinate | null>(null)
  const isDraggingRef = useRef(false)
  const bulkReplaceOnNextInputRef = useRef(false)

  // Adapter ref — kept current for use inside async event handlers that
  // cannot be recreated on every column change without performance cost.
  const adapterRef = useRef(adapter)
  useLayoutEffect(() => {
    adapterRef.current = adapter
  })

  // Keep clipboard snapshot refs in sync after each render
  useEffect(() => {
    selectedCellKeysForClipboardRef.current = selectedCellKeys
  }, [selectedCellKeys])

  useEffect(() => {
    anchorCellKeyForClipboardRef.current = anchorCellKey
  }, [anchorCellKey])

  const setAnchorCellKey = (key: string | null) => {
    setAnchorCellKeyState(key)
    anchorCellKeyRef.current = key
  }

  const replaceSelection = useCallback((nextSelection: Set<string>) => {
    selectedCellKeysRef.current = nextSelection
    setSelectedCellKeys(nextSelection)
  }, [])

  const updateSelection = (updater: (previous: Set<string>) => Set<string>) => {
    setSelectedCellKeys((previous) => {
      const next = updater(previous)
      selectedCellKeysRef.current = next
      return next
    })
  }

  const clearSelection = () => {
    replaceSelection(new Set())
    setAnchorCellKey(null)
    bulkReplaceOnNextInputRef.current = false
    isDraggingRef.current = false
    dragAnchorRef.current = null
  }

  const clearSelectionRef = useRef(clearSelection)
  useLayoutEffect(() => {
    clearSelectionRef.current = clearSelection
  })

  const buildSelectionRectangle = useCallback(
    (start: CellCoordinate, end: CellCoordinate): Set<string> => {
      const minRow = Math.min(start.rowIndex, end.rowIndex)
      const maxRow = Math.max(start.rowIndex, end.rowIndex)
      const minCol = Math.min(start.columnIndex, end.columnIndex)
      const maxCol = Math.max(start.columnIndex, end.columnIndex)

      const nextSelection = new Set<string>()
      for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
        for (let colIndex = minCol; colIndex <= maxCol; colIndex++) {
          const key = adapterRef.current.getCellKey({ rowIndex, columnIndex: colIndex })
          if (key !== null) {
            nextSelection.add(key)
          }
        }
      }
      return nextSelection
    },
    [] // intentional: uses adapterRef for current adapter without re-creation
  )

  const handleCellMouseDown = (cellKey: string, ev: MouseEventLike) => {
    const currentCoord = adapterRef.current.getCoordinate(cellKey)
    if (!currentCoord) {
      return
    }

    const isCtrlPressed = ev.ctrlKey || ev.metaKey
    const isShiftPressed = Boolean(ev.shiftKey)
    const currentAnchorKey = anchorCellKeyRef.current ?? anchorCellKey

    if (isShiftPressed && currentAnchorKey) {
      const anchorCoord = adapterRef.current.getCoordinate(currentAnchorKey)
      if (!anchorCoord) {
        return
      }
      replaceSelection(buildSelectionRectangle(anchorCoord, currentCoord))
      bulkReplaceOnNextInputRef.current = true
      ev.preventDefault?.()
      return
    }

    if (isCtrlPressed) {
      updateSelection((previous) => {
        const next = new Set(previous)
        if (next.has(cellKey)) {
          next.delete(cellKey)
        } else {
          next.add(cellKey)
        }
        return next
      })
      setAnchorCellKey(cellKey)
      bulkReplaceOnNextInputRef.current = true
      ev.preventDefault?.()
      return
    }

    const currentSelection = selectedCellKeysRef.current
    const shouldKeepRange = currentSelection.size > 1 && currentSelection.has(cellKey)

    if (!shouldKeepRange) {
      replaceSelection(new Set([cellKey]))
      bulkReplaceOnNextInputRef.current = false
    }

    setAnchorCellKey(cellKey)
    dragAnchorRef.current = currentCoord
    isDraggingRef.current = true
  }

  const handleCellMouseOver = (cellKey: string) => {
    if (!isDraggingRef.current || !dragAnchorRef.current) {
      return
    }
    const currentCoord = adapterRef.current.getCoordinate(cellKey)
    if (!currentCoord) {
      return
    }
    replaceSelection(buildSelectionRectangle(dragAnchorRef.current, currentCoord))
    bulkReplaceOnNextInputRef.current = true
  }

  // Clear drag state on mouse up anywhere in the document
  useEffect(() => {
    function handleMouseUp(): void {
      isDraggingRef.current = false
      dragAnchorRef.current = null
    }
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  // Extend drag via document mousemove — fires even when browser captures
  // pointer inside an <input> and suppresses mouseover on sibling cells.
  // Reads data-cell-key from the <td> hit under the cursor.
  useEffect(() => {
    function handleDocumentMouseMove(ev: MouseEvent): void {
      const dragAnchor = dragAnchorRef.current
      if (!isDraggingRef.current || !dragAnchor) {
        return
      }
      const element = document.elementFromPoint(ev.clientX, ev.clientY)
      if (!element) {
        return
      }
      const td = (element as Element).closest<HTMLElement>('[data-cell-key]')
      if (!td) {
        return
      }
      const cellKey = td.dataset.cellKey
      if (!cellKey) {
        return
      }
      const currentCoord = adapterRef.current.getCoordinate(cellKey)
      if (!currentCoord) {
        return
      }
      replaceSelection(buildSelectionRectangle(dragAnchor, currentCoord))
      bulkReplaceOnNextInputRef.current = true
    }

    document.addEventListener('mousemove', handleDocumentMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove)
    }
  }, [buildSelectionRectangle, replaceSelection])

  // Escape key clears selection while focus is inside the table container
  useEffect(() => {
    function handleEscape(ev: KeyboardEvent): void {
      if (ev.key !== 'Escape') {
        return
      }
      if (tableContainerRef.current && !tableContainerRef.current.contains(ev.target as Node)) {
        return
      }
      clearSelectionRef.current()
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [tableContainerRef])

  // Click outside the table container clears selection
  useEffect(() => {
    function handleOutsideMouseDown(event: MouseEvent): void {
      if (!tableContainerRef.current) {
        return
      }
      if (tableContainerRef.current.contains(event.target as Node)) {
        return
      }
      clearSelectionRef.current()
    }
    document.addEventListener('mousedown', handleOutsideMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown)
    }
  }, [tableContainerRef])

  // Prune stale cell keys when rows are removed or columns become hidden
  useEffect(() => {
    if (selectedCellKeysRef.current.size === 0) {
      return
    }
    const validSelection = new Set<string>()
    selectedCellKeysRef.current.forEach((cellKey) => {
      if (adapter.getCoordinate(cellKey) !== null) {
        validSelection.add(cellKey)
      }
    })
    if (validSelection.size !== selectedCellKeysRef.current.size) {
      replaceSelection(validSelection)
    }
    if (anchorCellKeyRef.current && adapter.getCoordinate(anchorCellKeyRef.current) === null) {
      setAnchorCellKeyState(null)
      anchorCellKeyRef.current = null
    }
  }, [adapter, replaceSelection])

  function getEditTargets(cellKey: string): ReadonlySet<string> {
    const selectedKeys = selectedCellKeysRef.current
    return selectedKeys.size > 1 && selectedKeys.has(cellKey) ? selectedKeys : new Set([cellKey])
  }

  function takeReplaceOnType(cellKey: string): boolean {
    const isArmed = bulkReplaceOnNextInputRef.current
    bulkReplaceOnNextInputRef.current = false
    return isArmed && getEditTargets(cellKey).size > 1
  }

  return {
    selectedCellKeys,
    anchorCellKey,
    selectedCellKeysRef,
    anchorCellKeyRef,
    selectedCellKeysForClipboardRef,
    anchorCellKeyForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    updateSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets,
    takeReplaceOnType,
  }
}
